import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasChunkError: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check for chunk loading or unexpected HTML token errors across all browsers
    const msg = error?.message || "";
    const name = error?.name || "";
    const isChunkError =
      name === "ChunkLoadError" ||
      msg.includes("Loading chunk") ||
      msg.includes("Unexpected token '<'") ||
      msg.includes("expected expression, got '<'") ||
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("is not valid JSON") ||
      (name === "SyntaxError" && (msg.includes("<") || msg.includes("token")));

    if (isChunkError) {
      return { hasChunkError: true };
    }

    // Pass through normal non-chunk errors to global handlers
    return { hasChunkError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.state.hasChunkError) {
      console.warn("ChunkErrorBoundary caught a stale chunk load error:", error, errorInfo);
    }
  }

  handleReload = async () => {
    try {
      if (typeof window !== "undefined" && "caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (e) {
      console.warn("Failed to clear caches on reload:", e);
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="flex h-full w-full min-h-[300px] flex-1 items-center justify-center p-6 select-none animate-fadeIn">
          <div className="flex flex-col items-center justify-center space-y-3.5 text-center max-w-sm">
            <div className="w-12 h-12 rounded-full bg-orange-50 border border-orange-200 flex items-center justify-center text-[#FF6B1A]">
              <svg
                className="w-6 h-6 animate-pulse"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <h4 className="text-sm font-semibold text-slate-800">
              Module Update Detected
            </h4>
            <p className="text-xs text-slate-500">
              A newer version of this module is available. Please reload to sync the latest assets.
            </p>
            <button
              onClick={this.handleReload}
              className="mt-2 px-4 py-2 text-xs font-semibold text-white bg-[#FF6B1A] hover:bg-orange-600 active:scale-95 transition-all rounded-lg shadow-sm shadow-orange-500/20 cursor-pointer"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
